import { Link } from "react-router-dom";
import type { TreningListaDto } from "@shared/treningTypy.js";
import type { StanZasobu } from "../../hooks/useZasob.js";
import { formatDatyTreningu, opisBleduZKodu, plakietkaListy, skrocTekst } from "../../lib/trening.js";
import { odmien } from "../../lib/polski.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { StanBledu, StanLadowania, StanPusty } from "../ui/stany.js";
import { PlakietkaOceny } from "./PlakietkaOceny.js";

/**
 * Karta "Twoje ćwiczenia": treningi tego konta od najnowszych. Cztery stany (P-6) w tej samej karcie (S-5): ladowanie,
 * blad z ponowieniem, pusty (z zacheta) i dane. Lista nie odswieza sie sama: wraca sie tu po wejsciu na ekran.
 *
 * Wiersz niesie parametr, skrocony tekst cwiczenia, poziom, date i liczbe odpowiedzi, plakietke ("Trwa" albo ocena
 * slowna albo "Za mało danych") i jeden przycisk: "Wróć do ćwiczenia" dla otwartego, "Zobacz wynik" dla zakonczonego.
 */
export function ListaTreningow({ zasob }: { zasob: StanZasobu<TreningListaDto[]> }): React.JSX.Element {
  const { dane, ladowanie, blad, statusBledu, odswiez } = zasob;

  let tresc: React.JSX.Element;
  if (dane === null && blad !== null) {
    tresc = (
      <StanBledu
        tytul="Nie udało się wczytać Twoich ćwiczeń"
        opis={opisBleduZKodu(blad, statusBledu).zdanie}
        akcja={
          <Button wariant="secondary" onClick={odswiez}>
            Spróbuj ponownie
          </Button>
        }
      />
    );
  } else if (dane === null || (ladowanie && dane.length === 0)) {
    tresc = <StanLadowania tekst="Wczytuję Twoje ćwiczenia..." />;
  } else if (dane.length === 0) {
    tresc = (
      <StanPusty
        tytul="Nie masz jeszcze ćwiczeń"
        opis="Wybierz ćwiczenie powyżej i zacznij pierwszą rozmowę. Tu zostanie ślad Twoich prób i ocen."
      />
    );
  } else {
    tresc = (
      <ul className="rowlist">
        {dane.map((t) => (
          <WierszTreningu key={t.id} trening={t} />
        ))}
      </ul>
    );
  }

  return <Card tytul="Twoje ćwiczenia">{tresc}</Card>;
}

function WierszTreningu({ trening }: { trening: TreningListaDto }): React.JSX.Element {
  const data = formatDatyTreningu(trening.utworzono);
  const odpowiedzi = `${trening.liczbaTurUcznia} ${odmien(trening.liczbaTurUcznia, "odpowiedź", "odpowiedzi", "odpowiedzi")}`;
  const meta = [`Poziom: ${trening.poziomNazwa}`, data, odpowiedzi].filter(Boolean).join(" · ");
  const trwa = trening.status === "ACTIVE";

  return (
    <li className="rowlist__row trening-wiersz">
      <div className="trening-wiersz__tresc">
        <p className="rowlist__nazwa">{trening.parametr}</p>
        <p className="trening-wiersz__opis">{skrocTekst(trening.cwiczenieTekst)}</p>
        <p className="rowlist__meta">{meta}</p>
      </div>
      <div className="trening-wiersz__akcje">
        <PlakietkaOceny plakietka={plakietkaListy(trening)} />
        <Link className="btn btn--secondary" to={`/cwiczenia/${encodeURIComponent(trening.id)}`}>
          {trwa ? "Wróć do ćwiczenia" : "Zobacz wynik"}
          {/* Wiersze maja ten sam napis, wiec czytnik ekranu dostaje jeszcze, czego dotyczy (A-5). */}
          <span className="tylko-dla-czytnika">: {trening.parametr}</span>
        </Link>
      </div>
    </li>
  );
}
